import React, { memo } from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
// import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import { combineClasses } from "@/lib/utils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface Dataset {
  label: string;
  data: number[];
  backgroundColor?: string;
}

interface GenericBarGraphProps {
  title?: string;
  labels: string[];
  datasets: Dataset[];
  height?: number | string;
  options?: any;
  className?:string;
  IsLegendshown?: boolean;
  /** Narrow/mobile layout: full width, horizontal scroll when needed */
  compact?: boolean;
}

const GenericBarGraph: React.FC<GenericBarGraphProps> = ({
  title,
  labels,
  datasets,
  height = 250,
  options,
  className,
  IsLegendshown = true,
  compact = false,
}) => {
  const isScrollable = compact ? labels.length > 3 : labels.length > 14;

  const barData = {
    labels,
    datasets: datasets.map((ds) => ({
      ...ds,
      barThickness: 30,
    })),
  };

  const barOptions =
    options || {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom" as const,
          display: IsLegendshown,
          labels: compact
            ? { boxWidth: 10, font: { size: 10 }, padding: 10 }
            : undefined,
        },
        datalabels: {
          anchor: "end",
          align: "top",
          color: "#333",
          font: { weight: "bold" as const, size: 10 },
          formatter: (value: number | undefined | null) => String(value ?? 0),
        },
      },
      // scales: {
      //   x: {
      //     grid: { display: false },
      //     ticks: compact
      //       ? { maxRotation: 90, minRotation: 45, autoSkip: true }
      //       : undefined,
      //   },
      //   y: {
      //     beginAtZero: true,
      //     grid: { color: "#f0f0f0" },
      //   },
      // },
      scales: {
        x: {
          type: "category" as const,          
          ticks: {
            callback: function (value: any) {
              return (this as any).getLabelForValue(value);
            },
            ...(compact ? { maxRotation: 90, minRotation: 45, autoSkip: true } : {}),
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          grid: { color: "#f0f0f0" },
        },
      },
    };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: compact ? 0 : 2,
        p: compact ? 1 : 2,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        margin: 0,
      }}
      className={combineClasses(className)}
    >
      <Typography fontWeight={600} mb={1} sx={{ textDecoration: "underline" }}>
        {title}
      </Typography>
      <Box
        sx={{
          overflowX: isScrollable ? "auto" : "visible",
          maxWidth: "100%",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            height,
            width: isScrollable ? "max-content" : "100%",
            minWidth: isScrollable ? `${labels.length * (compact ? 64 : 80)}px` : "100%",
          }}
        >
          <Bar data={barData} options={barOptions} />
        </Box>
      </Box>
    </Box>
  );
};

export default memo(GenericBarGraph);
